import {
  API_URLBASE,
  ClassroomsNoNulls,
  Grade,
  SettingsGrades,
  StudentConditionsNoNulls,
  StudentsNoNulls,
  TeacherConditionsNoNulls,
  TeachersNoNulls,
} from ".";

const psFetch = async (
  method: "DELETE" | "GET" | "POST" | "PUT",
  endpoint: string,
  payload: Object = {},
): Promise<Object | Array<Object>> => {
  try {
    const res = await fetch(`${API_URLBASE}/${endpoint}`, {
      method,
      mode: "cors",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      ...(method !== "GET" && { body: JSON.stringify(payload) }),
    });
    const result = await res.json();
    return !res.ok ? { error: result.error?.toString() } : result;
  } catch (err: any) {
    return { error: "Not logged in" };
  }
};

export const fetchAuthValidateToken = async (): Promise<{ error?: string; role?: string; username?: string }> => {
  try {
    const res = await psFetch("GET", "validateToken");
    if (Object.keys(res).includes("error")) {
      return { error: (res as any).error?.toString() };
    } else {
      const { role, username } = res as any;
      return { role, username };
    }
  } catch (err: any) {
    return { error: err };
  }
};

export const fetchAuthLogin = async (
  username: string,
  password: string,
): Promise<{ error?: string; username?: string }> => {
  try {
    const res = await psFetch("POST", "login", { username, password });
    const result = res as { error?: string; username?: string };
    if (Object.keys(result).includes("error")) {
      return { error: result.error };
    }
    return { username: result.username };
  } catch (err: any) {
    console.error(err);
    return err;
  }
};

export const fetchAuthRegisterSubmit = async (
  username: string,
  password: string,
): Promise<{ error?: string; username: string }> => {
  try {
    const res = await fetch(`${API_URLBASE}/register1`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const registered = await res.json();
    if (Object.keys(registered).includes("error")) console.error(registered);
    return registered;
  } catch (err: any) {
    console.error(err);
    return err;
  }
};

export const fetchAuthRegisterWith2FA = async (
  username: string,
  password: string,
  code: string,
): Promise<{ error?: string; username: string }> => {
  try {
    const res = await fetch(`${API_URLBASE}/register2`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ username, password, code }),
    });
    const registered = await res.json();
    if (Object.keys(registered).includes("error")) console.error(registered);
    return registered;
  } catch (err: any) {
    console.error(err);
    return err;
  }
};

export const fetchAuthForgotSubmit = async (username: string): Promise<{ error?: string; username: string }> => {
  try {
    const res = await fetch(`${API_URLBASE}/forgot1`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    return res.json();
  } catch (err: any) {
    console.error(err);
    return err;
  }
};

export const fetchAuthForgotWith2FA = async (
  username: string,
  code: string,
  password: string,
): Promise<{ error?: string; username: string }> => {
  try {
    const res = await fetch(`${API_URLBASE}/forgot2`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ username, code, password }),
    });
    return res.json();
  } catch (err: any) {
    console.error(err);
    return err;
  }
};

export const fetchAuthLogout = async (): Promise<boolean> => {
  try {
    const res = (await psFetch("POST", "logout")) as { error?: string; username?: string };
    return !Object.keys(res).includes("error");
  } catch (err: any) {
    console.error(err);
    return false;
  }
};

export const fetchDeleteClassrooms = async (toDelete: ClassroomsNoNulls[]) =>
  psFetch("DELETE", "classrooms", { toDelete });
export const fetchDeleteClassroomsAll = async () => psFetch("DELETE", "classrooms/all");
export const fetchDeleteStudentConditions = async (toDelete: StudentConditionsNoNulls[]) =>
  psFetch("DELETE", "studentconditions", { toDelete });
export const fetchDeleteStudentConditionsAll = async () => psFetch("DELETE", "studentconditions/all");
export const fetchDeleteStudents = async (toDelete: StudentsNoNulls[]) => psFetch("DELETE", "students", { toDelete });
export const fetchDeleteStudentsAll = async () => psFetch("DELETE", "students/all");
export const fetchDeleteTeacherConditions = async (toDelete: TeacherConditionsNoNulls[]) =>
  psFetch("DELETE", "teacherconditions", { toDelete });
export const fetchDeleteTeacherConditionsAll = async () => psFetch("DELETE", "teacherconditions/all");
export const fetchDeleteTeachers = async (toDelete: TeachersNoNulls[]) => psFetch("DELETE", "teachers", { toDelete });
export const fetchDeleteTeachersAll = async () => psFetch("DELETE", "teachers/all");

export const fetchGetClassroomsAll = async () => psFetch("GET", "classrooms/all");
export const fetchGetClassroomsAllV = async () => psFetch("GET", "classrooms/all/v");
export const fetchGetFailed = async () => psFetch("GET", "failed");
export const fetchGetSettingsAll = async () => psFetch("GET", "settings/all");
export const fetchGetSortProgress = (sortOperationId: string) =>
  new EventSource(`${API_URLBASE}/sort/progress/${sortOperationId}`);
export const fetchGetSorted = async () => psFetch("GET", "sorted");
export const fetchGetStudentConditionsAll = async () => psFetch("GET", "studentconditions/all");
export const fetchGetStudentsAll = async () => psFetch("GET", "students/all");
export const fetchGetStudentStuConds = async (stu_id: number) => psFetch("GET", `students/${stu_id}/stuconds`);
export const fetchGetStudentTchConds = async (stu_id: number) => psFetch("GET", `students/${stu_id}/tchconds`);
export const fetchGetTeacherConditionsAll = async () => psFetch("GET", "teacherconditions/all");
export const fetchGetTeachersAll = async () => psFetch("GET", "teachers/all");

export const fetchPostClassrooms = async (toAdd: ClassroomsNoNulls[]) => psFetch("POST", "classrooms", { toAdd });
export const fetchPostSort = async (grades: Grade[]) => psFetch("POST", "sort", { grades });
export const fetchPostStudentConditions = async (toAdd: StudentConditionsNoNulls[]) =>
  psFetch("POST", "studentconditions", { toAdd });
export const fetchPostStudents = async (toAdd: StudentsNoNulls[]) => psFetch("POST", "students", { toAdd });
export const fetchPostTeacherConditions = async (toAdd: TeacherConditionsNoNulls[]) =>
  psFetch("POST", "teacherconditions", { toAdd });
export const fetchPostTeachers = async (toAdd: TeachersNoNulls[]) => psFetch("POST", "teachers", { toAdd });

export const fetchPutClassroom = async (toUpdate: ClassroomsNoNulls) =>
  psFetch("PUT", `classrooms/${toUpdate.id}`, { toUpdate });
export const fetchPutSettingsGrades = async (toUpdate: SettingsGrades) =>
  psFetch("PUT", "settings/grades", { toUpdate });
export const fetchPutStudent = async (toUpdate: StudentsNoNulls) =>
  psFetch("PUT", `students/${toUpdate.id}`, { toUpdate });
export const fetchPutStudentCondition = async (toUpdate: StudentConditionsNoNulls) =>
  psFetch("PUT", `studentconditions/${toUpdate.id}`, { toUpdate });
export const fetchPutStudentsIncrementExceptFor = async (withholds: number[]) =>
  psFetch("PUT", "students/increment", { withholds });
export const fetchPutTeacher = async (toUpdate: TeachersNoNulls) =>
  psFetch("PUT", `teachers/${toUpdate.id}`, { toUpdate });
export const fetchPutTeacherCondition = async (toUpdate: TeacherConditionsNoNulls) =>
  psFetch("PUT", `teacherconditions/${toUpdate.id}`, { toUpdate });
